import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  SUPPORT_TICKET_CATEGORY_LABELS,
  SUPPORT_TICKET_STATUS_LABELS,
  type SupportTicket,
  type SupportTicketListResponse,
} from '@nexus/shared';

import { ApiError, apiGet } from '../lib/api';
import {
  Badge,
  Card,
  CellNote,
  DataTable,
  EmptyState,
  LoadErrorPage,
  LoadingPage,
  PageHeader,
  Stat,
  StatGrid,
  When,
  statusTone,
  type Column,
} from '../ui';

/**
 * The creator's tickets.
 *
 * Newest activity first, with the status in THEIR words — "Waiting on you"
 * is a turn, not an alarm — and an unread count on the rows that have a
 * reply they have not opened. The page exists so that "how do I contact
 * anybody" has an answer that is a nav item, not a hunt for an email
 * address.
 */
export function Support(): React.JSX.Element {
  const [data, setData] = useState<SupportTicketListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await apiGet<SupportTicketListResponse>('/support/tickets'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your tickets.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && data === null) {
    return (
      <LoadErrorPage eyebrow="Creator" title="Support" heading="Could not load your tickets." onRetry={() => void load()}>
        <span>{error}</span>
      </LoadErrorPage>
    );
  }
  if (data === null) return <LoadingPage eyebrow="Creator" title="Support" stats />;

  const live = data.tickets.filter((t) => t.status === 'OPEN' || t.status === 'WAITING_ON_CREATOR');
  const waitingOnYou = data.tickets.filter((t) => t.status === 'WAITING_ON_CREATOR');

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="Support"
        lead="A rejected clip you want looked at again, a payout that has not arrived, a referral that never paid — open a ticket and a person answers on it."
        actions={
          <Link className="btn btn-primary" to="/support/new">
            New ticket
          </Link>
        }
      />

      <StatGrid>
        <Stat label="Open" value={live.length} note="Tickets still in conversation." />
        <Stat
          label="Waiting on you"
          value={waitingOnYou.length}
          note="Nexus replied and is waiting for your answer."
          emphasis={waitingOnYou.length > 0}
        />
        <Stat
          label="Unread replies"
          value={data.unread.messages}
          note={data.unread.tickets > 0 ? `On ${data.unread.tickets} ticket${data.unread.tickets === 1 ? '' : 's'}.` : 'Nothing new.'}
          emphasis={data.unread.messages > 0}
        />
      </StatGrid>

      <Card title="Your tickets" sub="Newest activity first. Open one to read the thread and reply.">
        <TicketTable tickets={data.tickets} />
      </Card>
    </>
  );
}

function TicketTable({ tickets }: { tickets: SupportTicket[] }): React.JSX.Element {
  const columns: Array<Column<SupportTicket>> = [
    {
      key: 'subject',
      header: 'Ticket',
      primary: true,
      cell: (t) => (
        <>
          <Link to={`/support/${t.id}`}>{t.subject}</Link>
          <CellNote>{SUPPORT_TICKET_CATEGORY_LABELS[t.category]}</CellNote>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (t) => (
        <>
          <Badge tone={statusTone(t.status, 'support')} testId="ticket-status">
            {SUPPORT_TICKET_STATUS_LABELS[t.status]}
          </Badge>
          {t.unread > 0 && (
            <CellNote>
              {t.unread} unread {t.unread === 1 ? 'reply' : 'replies'}
            </CellNote>
          )}
        </>
      ),
    },
    {
      key: 'messages',
      header: 'Messages',
      numeric: true,
      align: 'end',
      cell: (t) => t.messageCount,
    },
    { key: 'last', header: 'Last activity', cell: (t) => <When value={t.lastMessageAt} withTime /> },
    { key: 'opened', header: 'Opened', cell: (t) => <When value={t.createdAt} /> },
  ];

  return (
    <DataTable
      columns={columns}
      rows={tickets}
      getKey={(t) => t.id}
      isHighlighted={(t) => t.unread > 0}
      empty={
        <EmptyState
          title="You have not opened a ticket."
          action={
            <Link className="btn btn-primary" to="/support/new">
              Open one
            </Link>
          }
        >
          Anything about a submission, a payout, a referral or a connected account — a person reads
          every one.
        </EmptyState>
      }
    />
  );
}
